/**
 * Login Page - QR-code based authentication
 *
 * Users scan their key backup QR code to log in.
 * No username/password required.
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  Divider,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { QrScanner, StoredKeyPair, useCryptoKeyStore } from '../features/encryption';
import { verifyKey } from '../features/encryption/api/keysApi';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const {
    activeKey,
    isInitialized,
    initialize,
    importAndActivateKey,
  } = useCryptoKeyStore();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  // Initialize key store
  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [isInitialized, initialize]);

  // Redirect if already logged in
  useEffect(() => {
    if (isInitialized && activeKey) {
      navigate(from, { replace: true });
    }
  }, [isInitialized, activeKey, navigate, from]);

  // Handle successful QR scan
  const handleKeyScanned = useCallback(
    async (keyPair: StoredKeyPair) => {
      setError(null);
      setIsVerifying(true);

      try {
        // Verify the key with the backend
        const verifyResult = await verifyKey(keyPair.keyId);

        if (!verifyResult.valid) {
          setError('Dieser Schlüssel ist nicht mehr gültig oder wurde widerrufen.');
          setIsVerifying(false);
          return;
        }

        // Update key with backend metadata
        const updatedKeyPair: StoredKeyPair = {
          ...keyPair,
          ownerType: verifyResult.ownerType,
          ownerName: verifyResult.ownerName,
        };

        // Import and activate the key
        await importAndActivateKey(updatedKeyPair);

        // Navigate to destination
        navigate(from, { replace: true });
      } catch (err) {
        // If verification fails (e.g., backend not available or new key),
        // still allow login with the key
        console.warn('Key verification failed, proceeding with limited access:', err);

        try {
          await importAndActivateKey(keyPair);
          navigate(from, { replace: true });
        } catch (importError) {
          setError(
            importError instanceof Error
              ? importError.message
              : 'Fehler beim Importieren des Schlüssels.'
          );
          setIsVerifying(false);
        }
      }
    },
    [importAndActivateKey, navigate, from]
  );

  // Handle scan error
  const handleScanError = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Antragsmanager
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              Scannen Sie Ihren QR-Code, um sich anzumelden
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {isVerifying ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body1">
                  Schlüssel wird überprüft...
                </Typography>
              </Box>
            ) : showScanner ? (
              <Box>
                <QrScanner
                  onKeyScanned={handleKeyScanned}
                  onError={handleScanError}
                  height={300}
                  showFileUpload={true}
                  instructions="Halten Sie den QR-Code von Ihrem Schlüssel-Dokument vor die Kamera."
                />

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowScanner(false)}
                  sx={{ mt: 2 }}
                >
                  Abbrechen
                </Button>
              </Box>
            ) : (
              <Box>
                {/* Scan QR button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<QrCodeScannerIcon />}
                  onClick={() => setShowScanner(true)}
                  sx={{ py: 1.5 }}
                >
                  QR-Code scannen
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    oder
                  </Typography>
                </Divider>

                {/* Register button */}
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<PersonAddIcon />}
                  onClick={() => navigate('/register')}
                  sx={{ py: 1.5 }}
                >
                  Neues Konto erstellen
                </Button>
              </Box>
            )}

            {/* Info box */}
            <Box
              sx={{
                mt: 4,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                So funktioniert die Anmeldung:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1. Öffnen Sie Ihr Schlüssel-PDF Dokument
              </Typography>
              <Typography variant="body2" color="text.secondary">
                2. Scannen Sie den QR-Code mit der Kamera
              </Typography>
              <Typography variant="body2" color="text.secondary">
                3. Sie sind automatisch angemeldet
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
